// frontend/src/services/tracking.js
// Tracking Service for Alertique Frontend
// Handles API calls for user item tracking functionality

import axios from "axios";
import { getApiBaseUrl } from "../config/api.js";

// Dynamic API URL from configuration
const getAPI_URL = () => getApiBaseUrl();

/**
 * DEPRECATED: Basic Auth with stored password is no longer supported for security reasons.
 * @returns {Object} Always returns empty object.
 */
const getAuthHeaders = () => {
  return {};
};

/**
 * Generates JWT Authentication headers from stored JWT token.
 * @returns {Object} An object containing the Authorization and Content-Type headers.
 * @returns {Object} An empty object if JWT token is not found in localStorage.
 */
const getJwtHeaders = () => {
  const token = localStorage.getItem("jwtToken");
  if (token) {
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }
  return {};
};

/**
 * Gets authentication headers using JWT only (password storage removed for security).
 * @returns {Object} Authentication headers object with JWT token if available.
 */
const getAuthHeadersWithJwt = () => {
  // Use JWT only (no password storage)
  return getJwtHeaders();
};

/**
 * Get all tracked items for the current user.
 * @returns {Promise<Array>} Array of tracked items
 * @throws {Error} If request fails
 */
export const getTrackedItems = async () => {
  console.log("tracking.js: Fetching tracked items"); // DEBUG
  console.log("tracking.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const response = await axios.get(`${getAPI_URL()}/api/track/items`, {
      headers: getAuthHeadersWithJwt(), // Use JWT-aware headers
    });
    console.log(
      "tracking.js: Tracked items fetched successfully:",
      response.data
    ); // DEBUG
    console.log("tracking.js: First item structure:", response.data[0]); // DEBUG
    return response.data;
  } catch (error) {
    // Handle 401 (Unauthorized) gracefully - token expired or not authenticated
    if (error.response?.status === 401) {
      // Don't log as error - this is expected when token expires
      return [];
    }
    console.error("tracking.js: Error fetching tracked items:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to fetch tracked items: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while fetching tracked items."
      );
    }
  }
};

/**
 * Track an item for the current user.
 * @param {number} itemId - The ID of the item to track
 * @param {number} alertThreshold - Optional price threshold for alerts
 * @returns {Promise<Object>} Response data from the backend
 * @throws {Error} If request fails
 */
export const trackItem = async (itemId, alertThreshold = null) => {
  console.log(`tracking.js: Tracking item ${itemId}`); // DEBUG
  console.log("tracking.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const payload = { item_id: itemId };
    if (alertThreshold !== null) {
      payload.alert_threshold = alertThreshold;
    }

    const response = await axios.post(
      `${getAPI_URL()}/api/user/track/item`,
      payload,
      { headers: getAuthHeadersWithJwt() } // Use JWT-aware headers
    );
    console.log("tracking.js: Item tracked successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("tracking.js: Error tracking item:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to track item: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error("An unexpected error occurred while tracking item.");
    }
  }
};

/**
 * Stop tracking an item for the current user.
 * @param {number} trackingId - The ID of the tracking record to delete
 * @returns {Promise<Object>} Response data from the backend
 * @throws {Error} If request fails
 */
export const untrackItem = async (trackingId) => {
  console.log(`tracking.js: Untracking item ${trackingId}`); // DEBUG
  console.log("tracking.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const response = await axios.delete(
      `${getAPI_URL()}/api/track/items/${trackingId}`,
      { headers: getAuthHeadersWithJwt() } // Use JWT-aware headers
    );
    console.log("tracking.js: Item untracked successfully:", response.data); // DEBUG
    return response.data;
  } catch (error) {
    console.error("tracking.js: Error untracking item:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to untrack item: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error("An unexpected error occurred while untracking item.");
    }
  }
};

/**
 * Update tracking status for an item.
 * @param {number} trackingId - The ID of the tracking record
 * @param {string} newStatus - The new tracking status
 * @returns {Promise<Object>} Response data from the backend
 * @throws {Error} If request fails
 */
export const updateTrackingStatus = async (trackingId, newStatus) => {
  console.log(`tracking.js: Updating tracking ${trackingId} to ${newStatus}`); // DEBUG
  console.log("tracking.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const response = await axios.put(
      `${getAPI_URL()}/api/track/items/${trackingId}`,
      { tracking_status: newStatus },
      { headers: getAuthHeadersWithJwt() } // Use JWT-aware headers
    );
    console.log(
      "tracking.js: Tracking status updated successfully:",
      response.data
    ); // DEBUG
    return response.data;
  } catch (error) {
    console.error("tracking.js: Error updating tracking status:", error); // DEBUG
    if (error.response) {
      throw new Error(
        error.response.data.error ||
          `Failed to update tracking status: ${error.response.status}`
      );
    } else if (error.request) {
      throw new Error("Network error. Please ensure the backend is running.");
    } else {
      throw new Error(
        "An unexpected error occurred while updating tracking status."
      );
    }
  }
};

/**
 * Get tracking statistics for the current user.
 * @returns {Promise<Object>} Tracking statistics
 * @throws {Error} If request fails
 */
export const getTrackingStats = async () => {
  console.log("tracking.js: Fetching tracking statistics"); // DEBUG
  console.log("tracking.js: Using API URL:", getAPI_URL()); // DEBUG

  try {
    const trackedItems = await getTrackedItems();

    // Calculate statistics
    const stats = {
      totalTracked: trackedItems.length,
      activeTracked: trackedItems.filter(
        (item) => item.tracking_status === "Active"
      ).length,
      soldOutTracked: trackedItems.filter(
        (item) => item.tracking_status === "Sold Out"
      ).length,
      fullPriceTracked: trackedItems.filter(
        (item) => item.tracking_status === "Full Price"
      ).length,
      categories: {},
      averageDiscount: 0,
    };

    // Calculate category distribution
    trackedItems.forEach((item) => {
      const category = item.item.category || "Unknown";
      stats.categories[category] = (stats.categories[category] || 0) + 1;
    });

    // Calculate average discount
    const itemsWithDiscount = trackedItems.filter(
      (item) =>
        item.item.discount_percentage && item.item.discount_percentage > 0
    );
    if (itemsWithDiscount.length > 0) {
      const totalDiscount = itemsWithDiscount.reduce(
        (sum, item) => sum + item.item.discount_percentage,
        0
      );
      stats.averageDiscount = Math.round(
        totalDiscount / itemsWithDiscount.length
      );
    }

    console.log("tracking.js: Tracking statistics calculated:", stats); // DEBUG
    return stats;
  } catch (error) {
    console.error("tracking.js: Error calculating tracking statistics:", error); // DEBUG
    throw error;
  }
};
